import process from 'node:process';
import { resolveAuthSource } from './auth';
import { runDebugSession } from './debug-session';
import { printTestHelp, resolveIsSuperadmin, showGlobalHelp } from './help.js';
import { runLogin } from './login';
import { runOrgs } from './orgs';
import { runRemoteTest } from './remote-test';

type CommandRunner = (rest: string[], command: string) => Promise<void>;
type CommandMetadataFlag = true | readonly string[];

export type CommandDefinition = {
  name: string;
  aliases?: readonly string[];
  updateCheck?: CommandMetadataFlag;
  handlesHelp?: CommandMetadataFlag;
  run: CommandRunner;
};

// Lazy-load playwright-dependent modules.
const loadMcp = () => import('./mcp').then((m) => m.runMcp);
const loadRecord = () => import('./record.js').then((m) => m.runRecord);
const loadSession = () => import('./session/index.js').then((m) => m.runSession);
const loadLocal = () => import('./local/index.js').then((m) => m.runLocal);

async function runDeprecatedTest(rest: string[]) {
  // Sub-command help
  if (rest.includes('--help') || rest.includes('-h')) {
    printTestHelp();
    return;
  }
  // Check for --remote flag — deprecated, forward to test-suite run
  if (rest.includes('--remote')) {
    console.warn(
      '\x1b[33m⚠ `canary test --remote` is deprecated and will be removed in a future release.\x1b[0m'
    );
    console.warn('  Use `canary test-suite run` instead.\n');
    const remoteArgs = rest.filter((arg) => arg !== '--remote');
    await runRemoteTest(remoteArgs);
    return;
  }
  // Local Playwright test runner has been removed
  console.error(
    '`canary test` (local Playwright runner) has been removed.\n' +
      'Use `canary test-suite run` for remote workflow tests.\n' +
      '\n' +
      'Examples:\n' +
      '  canary test-suite run\n' +
      '  canary test-suite run --tag smoke\n' +
      '  canary test-suite run --property "My App" --environment staging'
  );
  process.exit(1);
}

async function runSpec(rest: string[]) {
  const { resolveConfig } = await import('./auth.js');
  const { handleSpec } = await import('./spec/index.js');
  // `validate` is purely offline — don't fail on missing auth config.
  const wantsAuth = rest[0] === 'compile' || rest[0] === 'pull' || rest[0] === 'diff';
  if (wantsAuth) {
    const { apiUrl, token } = await resolveConfig(rest);
    await handleSpec(rest, apiUrl, token);
  } else {
    await handleSpec(rest, '', '');
  }
}

async function runApiCatalogCommand(rest: string[]) {
  const { resolveConfig } = await import('./auth.js');
  const { runApiCatalog } = await import('./api-catalog.js');
  const { apiUrl, token } = await resolveConfig(rest);
  await runApiCatalog(rest, apiUrl, token);
}

async function runStatusCommand(rest: string[], command: string) {
  // Superadmin-only. Gated client-side (the command talks to AWS directly,
  // not the cofactor API, so there is no server-side 403 to rely on). Treat
  // non-superadmins as if the command does not exist.
  if (!(await resolveIsSuperadmin())) {
    console.log(`Unknown command "${command}".`);
    await showGlobalHelp();
    process.exit(1);
  }
  const { runStatus } = await import('./status.js');
  await runStatus(rest);
}

async function runAuthCommand(rest: string[]) {
  const sub = rest[0];
  if (sub === 'doctor') {
    const { runAuthDoctor } = await import('./doctor.js');
    await runAuthDoctor(rest.slice(1));
    return;
  }
  console.error('Usage: canary auth doctor [--env <env>]');
  process.exit(1);
}

export const COMMAND_DEFINITIONS: readonly CommandDefinition[] = [
  {
    name: 'upgrade',
    run: async () => {
      const { runUpgrade } = await import('./upgrade.js');
      await runUpgrade();
    },
  },
  {
    name: 'test',
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => runDeprecatedTest(rest),
  },
  {
    name: 'mcp',
    run: async (rest) => {
      const runMcp = await loadMcp();
      await runMcp(rest);
    },
  },
  {
    name: 'install',
    handlesHelp: true,
    run: async (rest) => {
      const { runInstall } = await import('./install.js');
      await runInstall(rest);
    },
  },
  {
    name: 'login',
    updateCheck: true,
    run: async (rest) => runLogin(rest),
  },
  {
    name: 'orgs',
    aliases: ['org'],
    updateCheck: true,
    run: async (rest) => runOrgs(rest),
  },
  {
    name: 'record',
    updateCheck: true,
    run: async (rest) => {
      const runRecord = await loadRecord();
      await runRecord(rest);
    },
  },
  {
    name: 'session',
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => {
      const runSession = await loadSession();
      await runSession(rest);
    },
  },
  {
    name: 'local',
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => {
      const runLocal = await loadLocal();
      await runLocal(rest);
    },
  },
  {
    name: 'debug-session',
    run: async (rest) => runDebugSession(rest),
  },
  {
    name: 'psql',
    run: async (rest) => {
      const { runPsql } = await import('./psql.js');
      await runPsql(rest);
    },
  },
  {
    name: 'redis',
    run: async (rest) => {
      const { runRedis } = await import('./redis.js');
      await runRedis(rest);
    },
  },
  {
    name: 'release',
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => {
      const { runRelease } = await import('./release.js');
      await runRelease(rest);
    },
  },
  {
    name: 'planning',
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => {
      const { runPlanning } = await import('./planning.js');
      await runPlanning(rest);
    },
  },
  {
    name: 'docs',
    run: async (rest) => {
      const { runDocs } = await import('./docs.js');
      await runDocs(rest);
    },
  },
  {
    name: 'landing',
    run: async (rest) => {
      const { runLanding } = await import('./landing.js');
      await runLanding(rest);
    },
  },
  {
    name: 'feature-flag',
    run: async (rest) => {
      const { runFeatureFlag } = await import('./feature-flag.js');
      await runFeatureFlag(rest);
    },
  },
  {
    name: 'schedule',
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => {
      const { runSchedule } = await import('./schedule.js');
      await runSchedule(rest);
    },
  },
  {
    name: 'alert',
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => {
      const { runAlert } = await import('./alert.js');
      await runAlert(rest);
    },
  },
  {
    name: 'product',
    run: async (rest) => {
      const { runProduct } = await import('./product.js');
      await runProduct(rest);
    },
  },
  {
    name: 'metrics',
    run: async (rest) => {
      const { runMetrics } = await import('./metrics.js');
      await runMetrics(rest);
    },
  },
  {
    name: 'benchmark',
    aliases: ['benchmarks'],
    run: async (rest) => {
      const { runBenchmark } = await import('./benchmark.js');
      await runBenchmark(rest);
    },
  },
  {
    name: 'account',
    run: async (rest) => {
      const { runAccount } = await import('./account.js');
      await runAccount(rest);
    },
  },
  {
    name: 'scenario',
    aliases: ['scenarios'],
    run: async (rest) => {
      const { runScenarios } = await import('./scenarios.js');
      await runScenarios(rest);
    },
  },
  {
    name: 'knobs',
    run: async (rest) => {
      const { runKnobs } = await import('./knobs.js');
      await runKnobs(rest);
    },
  },
  {
    name: 'issues',
    aliases: ['issue'],
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => {
      const { runIssues } = await import('./issues.js');
      await runIssues(rest);
    },
  },
  {
    name: 'credential',
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => {
      const { runCredential } = await import('./credential.js');
      await runCredential(rest);
    },
  },
  {
    name: 'persona',
    aliases: ['personas', 'credential-template', 'credential-templates'],
    run: async (rest) => {
      const { runCredentialTemplate } = await import('./credential-template.js');
      await runCredentialTemplate(rest);
    },
  },
  {
    name: 'workflow',
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => {
      const { runWorkflow } = await import('./workflow.js');
      await runWorkflow(rest);
    },
  },
  {
    name: 'spec',
    run: async (rest) => runSpec(rest),
  },
  {
    name: 'api-catalog',
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => runApiCatalogCommand(rest),
  },
  {
    name: 'skill',
    aliases: ['skills'],
    updateCheck: ['skill', 'skills'],
    handlesHelp: ['skill', 'skills'],
    run: async (rest) => {
      const { runSkill } = await import('./skill.js');
      await runSkill(rest);
    },
  },
  {
    name: 'prompts',
    aliases: ['prompt'],
    handlesHelp: ['prompts', 'prompt'],
    run: async (rest) => {
      const { runPrompts } = await import('./prompts.js');
      await runPrompts(rest);
    },
  },
  {
    name: 'task',
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => {
      const { runTask } = await import('./task.js');
      await runTask(rest);
    },
  },
  {
    name: 'test-suite',
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => {
      const { runTestSuite } = await import('./test-suite.js');
      await runTestSuite(rest);
    },
  },
  {
    name: 'external-run',
    aliases: ['external-runs'],
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => {
      const { runExternalRun } = await import('./external-run.js');
      await runExternalRun(rest);
    },
  },
  {
    name: 'triage',
    run: async (rest) => {
      const { runTriage } = await import('./triage.js');
      await runTriage(rest);
    },
  },
  {
    // "application" is the user-facing name; "property" stays as a backwards-compat alias.
    name: 'application',
    aliases: ['applications', 'property', 'properties'],
    run: async (rest) => {
      const { runProperty } = await import('./property.js');
      await runProperty(rest);
    },
  },
  {
    name: 'environment',
    aliases: ['env'],
    run: async (rest) => {
      const { runEnvironment } = await import('./environment.js');
      await runEnvironment(rest);
    },
  },
  {
    name: 'sandbox',
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => {
      const { runSandbox } = await import('./sandbox.js');
      await runSandbox(rest);
    },
  },
  {
    name: 'attachment',
    aliases: ['attachments'],
    updateCheck: ['attachment', 'attachments'],
    handlesHelp: ['attachment', 'attachments'],
    run: async (rest) => {
      const { runAttachment } = await import('./attachment.js');
      await runAttachment(rest);
    },
  },
  {
    name: 'utilities',
    updateCheck: true,
    handlesHelp: true,
    run: async (rest) => {
      const { runUtilities } = await import('./utilities.js');
      await runUtilities(rest);
    },
  },
  {
    name: 'tag',
    aliases: ['tags'],
    updateCheck: ['tag', 'tags'],
    handlesHelp: ['tag', 'tags'],
    run: async (rest) => {
      const { runTag } = await import('./tag.js');
      await runTag(rest);
    },
  },
  {
    name: 'trace',
    run: async (rest) => {
      const { runTrace } = await import('./trace.js');
      await runTrace(rest);
    },
  },
  {
    name: 'status',
    run: async (rest, command) => runStatusCommand(rest, command),
  },
  {
    name: 'auth',
    run: async (rest) => runAuthCommand(rest),
  },
];

function namesForMetadata(
  definition: CommandDefinition,
  value: CommandMetadataFlag | undefined
): readonly string[] {
  if (value === true) return [definition.name];
  return value ?? [];
}

const commandDefinitionMap = new Map<string, CommandDefinition>();

for (const definition of COMMAND_DEFINITIONS) {
  commandDefinitionMap.set(definition.name, definition);
  for (const alias of definition.aliases ?? []) {
    commandDefinitionMap.set(alias, definition);
  }
}

/** Commands that trigger the upgrade-available warning. */
export const COMMANDS_WITH_UPDATE_CHECK = new Set(
  COMMAND_DEFINITIONS.flatMap((definition) =>
    Array.from(namesForMetadata(definition, definition.updateCheck))
  )
);

/** Commands that handle --help themselves instead of showing global help. */
export const COMMANDS_WITH_HELP = new Set(
  COMMAND_DEFINITIONS.flatMap((definition) =>
    Array.from(namesForMetadata(definition, definition.handlesHelp))
  )
);

export function getCommandDefinition(command: string): CommandDefinition | undefined {
  return commandDefinitionMap.get(command);
}

export async function maybeCheckForUpdate(command: string, argv: string[], version: string) {
  if (!COMMANDS_WITH_UPDATE_CHECK.has(command)) return;
  if ((await resolveAuthSource(argv)) !== 'stored') return;

  const { checkForUpdate } = await import('./update-check.js');
  await checkForUpdate(version);
}

export async function executeCommand(command: string, rest: string[]) {
  const definition = getCommandDefinition(command);
  if (!definition) {
    console.log(`Unknown command "${command}".`);
    await showGlobalHelp();
    process.exit(1);
  }

  await definition.run(rest, command);
}
